import { useState, useEffect } from 'react';
import ArtZipButton from './ArtZipButton';
import BatchCompletion from './BatchCompletion';

function localBatchDate() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

const GROUP_BADGE = {
  cards: 'text-indigo-700 bg-indigo-100',
  stands: 'text-purple-700 bg-purple-100',
  mixed: 'text-amber-700 bg-amber-100',
};

/**
 * Process pending MSP store orders into cards/stands batches.
 * Mixed orders join both batches (production separates cards from stands).
 */
export default function MspModal({ onClose, onComplete, secretKey, onNeedAuth, onAuthFailed }) {
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmSubset, setConfirmSubset] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPending = async () => {
    setError(null);
    try {
      const res = await fetch('/api/msp/pending');
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to load pending orders');
      setData(d);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProcess = async (mode) => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/msp/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: secretKey, mode, batchDate: localBatchDate() }),
      });
      const d = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (res.status === 409) {
        setData({ orders: d.orders, counts: data?.counts });
        setConfirmSubset(false);
        setConfirmAll(false);
        setError(d.error);
        return;
      }
      if (!res.ok) throw new Error(d.error || 'Processing failed');
      setResult(d);
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const orders = data?.orders || [];
  const valid = orders.filter((o) => o.valid);
  const invalid = orders.filter((o) => !o.valid);
  const cardMembers = valid.filter((o) => o.cards > 0);
  const standMembers = valid.filter((o) => o.stands > 0);
  const date = localBatchDate();

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Process MSP Store Orders</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {result
                ? 'Batches created'
                : 'Orders are split into cards and stands batches for production'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!data && !error && !result && (
            <div className="py-10 flex items-center justify-center gap-2.5 text-sm text-gray-400">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
              Loading pending orders...
            </div>
          )}

          {/* Pending review */}
          {data && !result && (
            <>
              {/* Planned batches */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className={`rounded-lg px-4 py-3 border ${cardMembers.length > 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                  <p className="text-xs text-indigo-600 font-medium">Cards batch</p>
                  <p className="text-sm font-bold font-mono text-indigo-800 mt-1">MSP{date}_cards</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {cardMembers.length} order{cardMembers.length !== 1 ? 's' : ''}
                    {cardMembers.some((o) => o.group === 'mixed') &&
                      ` (${cardMembers.filter((o) => o.group === 'mixed').length} mixed)`}
                  </p>
                </div>
                <div className={`rounded-lg px-4 py-3 border ${standMembers.length > 0 ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                  <p className="text-xs text-purple-600 font-medium">Stands batch</p>
                  <p className="text-sm font-bold font-mono text-purple-800 mt-1">MSP{date}_stands</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {standMembers.length} order{standMembers.length !== 1 ? 's' : ''}
                    {standMembers.some((o) => o.group === 'mixed') &&
                      ` (${standMembers.filter((o) => o.group === 'mixed').length} mixed)`}
                  </p>
                </div>
              </div>

              {orders.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No pending MSP orders.</p>
              )}

              {invalid.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-red-800">
                  <span className="font-semibold">Review required:</span> {invalid.length} order
                  {invalid.length !== 1 ? 's have' : ' has'} issues. Fix them, or process the valid subset below.
                </div>
              )}

              {orders.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[38vh]">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Order</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Company</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Placed</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Cards</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Stands</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Group</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {orders.map((o) => (
                          <tr key={o.order_uid} className={o.valid ? '' : 'bg-red-50'}>
                            <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">{o.order_uid}</td>
                            <td className="px-4 py-2 text-xs text-gray-600">{o.company || '—'}</td>
                            <td className="px-4 py-2 text-xs text-gray-500">
                              {o.created_at
                                ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : '—'}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-600 tabular-nums">{o.cards}</td>
                            <td className="px-4 py-2 text-xs text-gray-600 tabular-nums">{o.stands}</td>
                            <td className="px-4 py-2 text-xs">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${GROUP_BADGE[o.group]}`}>
                                {o.group}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {o.valid ? (
                                <span className="text-emerald-600 font-medium">Ready</span>
                              ) : (
                                o.issues.map((issue, i) => (
                                  <span key={i} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                    {issue}
                                  </span>
                                ))
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Result */}
          {result && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {result.processed} Order{result.processed !== 1 ? 's' : ''} Processed
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-6 text-xs text-emerald-800 space-y-1">
                <p>
                  ✓ Batch{result.batches.length !== 1 ? 'es' : ''}{' '}
                  <span className="font-mono font-semibold">{result.batches.map((b) => b.label).join(', ')}</span> created
                </p>
                <p>✓ {result.updated ?? result.processed} order{(result.updated ?? result.processed) !== 1 ? 's' : ''} updated: status → <span className="font-mono font-semibold">production</span></p>
                <p>✓ send_authorized → <span className="font-mono font-semibold">TRUE</span></p>
              </div>
              <div className="w-full max-w-md space-y-3">
                {result.batches.map((b) => (
                  <div key={b.label} className="border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-mono font-semibold text-gray-900">{b.label}</p>
                      <p className="text-xs text-gray-400">
                        {b.orders} order{b.orders !== 1 ? 's' : ''} · {b.group}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <a
                        href={`/api/greenlight/export/${encodeURIComponent(b.label)}`}
                        download
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        CSV
                      </a>
                      <ArtZipButton
                        label={b.label}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                      >
                        Art ZIP
                      </ArtZipButton>
                    </div>
                  </div>
                ))}
              </div>
              {result.skipped.length > 0 && (
                <p className="text-xs text-amber-700 mt-5 max-w-md text-center">
                  Skipped (still pending): {result.skipped.map((s) => s.order_uid).join(', ')}
                </p>
              )}
              <BatchCompletion
                batches={result.batches.map((b) => ({ label: b.label, orders: b.orders }))}
                batchDate={localBatchDate()}
                secretKey={secretKey}
                onNeedAuth={onNeedAuth}
                onAuthFailed={onAuthFailed}
                onSent={onComplete}
              />
            </div>
          )}
        </div>

        {/* Full-run confirmation — states exactly what will change before writing */}
        {data && !result && confirmAll && (
          <div className="mx-6 mt-2 bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">
              Create{' '}
              {[
                cardMembers.length > 0 && <span key="c" className="font-mono">MSP{date}_cards ({cardMembers.length})</span>,
                standMembers.length > 0 && <span key="s" className="font-mono">MSP{date}_stands ({standMembers.length})</span>,
              ].filter(Boolean).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' and ', el]), [])}
              ?
            </p>
            <p className="text-xs text-emerald-800">
              This updates {valid.length} order{valid.length !== 1 ? 's' : ''}: status →{' '}
              <span className="font-mono font-semibold">production</span>, send_authorized →{' '}
              <span className="font-mono font-semibold">TRUE</span>, and stamps the batch label
              {cardMembers.length > 0 && standMembers.length > 0 ? 's' : ''}.
            </p>
          </div>
        )}

        {/* Subset confirmation — pinned above footer */}
        {data && !result && confirmSubset && (
          <div className="mx-6 mt-2 bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-amber-900 mb-1.5">
              Process {valid.length} of {orders.length} orders?
            </p>
            <p className="text-xs text-amber-800 mb-2">
              {invalid.length !== 1 ? `These ${invalid.length} orders` : 'This order'} will be{' '}
              <span className="font-semibold">skipped</span> and stay pending:
            </p>
            <p className="text-xs font-mono text-amber-900">
              {invalid.map((o) => `${o.order_uid} (${o.issues.join(', ')})`).join('; ')}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {result ? (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Done
            </button>
          ) : confirmAll ? (
            <>
              <button
                onClick={() => setConfirmAll(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleProcess('all')}
                disabled={loading}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Processing...' : `Yes — process ${valid.length} order${valid.length !== 1 ? 's' : ''}`}
              </button>
            </>
          ) : confirmSubset ? (
            <>
              <button
                onClick={() => setConfirmSubset(false)}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleProcess('subset')}
                disabled={loading}
                className="px-5 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Processing...' : `Yes — process ${valid.length}, skip ${invalid.length}`}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              {invalid.length > 0 && valid.length > 0 && (
                <button
                  onClick={() => setConfirmSubset(true)}
                  disabled={loading}
                  className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-100 disabled:opacity-40 transition-colors"
                >
                  Process valid subset ({valid.length} of {orders.length})…
                </button>
              )}
              <button
                onClick={() => setConfirmAll(true)}
                disabled={loading || orders.length === 0 || invalid.length > 0}
                title={invalid.length > 0 ? 'Fix all issues to process everything' : undefined}
                className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {`Process ${valid.length} Order${valid.length !== 1 ? 's' : ''}…`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
